<template>
  <div @click="selectedItem = null">
    <breadcrumbs home="Batch item move"/>

    <v-card flat>
      <v-form v-model="valid" ref="form">
        <v-container class="pt-6 px-6 pb-0 purtrak-scrollable" fluid>
          <v-row align="center">
            <v-col cols="6" sm="4" lg="3">
              <date-picker v-model="transaction.date" @change="refresh()" hide-details/>
            </v-col>
            <v-col cols="6" sm="4" lg="3">
              <v-text-field label="Status" v-model="transaction.status" :rules="[fieldRules.maxLength(20)]" counter="255" outlined dense hide-details/>
            </v-col>

            <template v-if="transaction.defaultFromSiteId && transaction.defaultToSiteId">
              <v-col cols="6" sm="4" lg="2">
                <v-combobox label="Ship via" v-model="transaction.shipVia" outlined dense :rules="[fieldRules.maxLength(20)]" hide-details :items="shipVias"/>
              </v-col>
              <v-col cols="6" sm="4" lg="1">
                <decimal-number-input label="Pallets" v-model="transaction.pallets" prefix="" :precision="0" hide-details/>
              </v-col>
              <v-col cols="6" sm="4" lg="2">
                <v-text-field label="Ref #" v-model="transaction.number" :rules="[fieldRules.maxLength(20)]" outlined dense hide-details/>
              </v-col>
            </template>

            <v-col cols="1" v-if="$vuetify.breakpoint.mobile">
              <scan-code @code="scan" :items="allItems"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <outlined-block :rules="[() => items.length>0 || 'Items required']">
                <v-data-table width="100%"
                              calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer fixed-header
                              :items="items" item-key="key" no-data-text="No item moves specified"
                              :headers="headers" class="no-sort mt-1"
                              :item-class="item => item.key=== selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="item => {selectedItem = item.key; $nextTick(() => this.$refs.form.validate())}">
                <template v-slot:header.action>
                  <scan-code @code="scan" :items="allItems"/>
                </template>
                <template v-slot:header.fromSite="{header}">
                  From site
                  <v-autocomplete dense v-model="transaction.defaultFromSiteId" :items="defaultSites" item-text="name" item-value="id" hide-details="" class="text-caption mb-2"
                                  @change="changeDefaultFromSiteId"/>
                </template>
                <template v-slot:header.toSite="{header}">
                  To site
                  <v-autocomplete dense v-model="transaction.defaultToSiteId" :items="defaultSites" item-text="name" item-value="id" hide-details="" class="text-caption mb-2"
                                  @change="changeDefaultToSiteId"/>
                </template>
                <template v-slot:item.action="{item,index}">
                  <v-btn icon @click="items.splice(index, 1)"><v-icon small>mdi-delete</v-icon></v-btn>
                </template>
                <template v-slot:item.item="{item}">
                  <v-autocomplete v-if="selectedItem === item.key" v-model="item.itemId" dense :items="allItems" item-text="name" item-value="id" :rules="[fieldRules.required]" class="text-caption" @change="selectItem(item)" autofocus outlined/>
                  <div v-else :class="{'error--text':!item.itemId}">{{itemName(item)}}</div>
                </template>
                <template v-slot:item.lot="{item}">
                  <template v-if="item.lotTracking">
                    <v-combobox  v-if="selectedItem === item.key" v-model="item.lot" dense :items="item.lots" :rules="[fieldRules.required,fieldRules.maxLength(30)]" class="text-caption" outlined/>
                    <div v-else :class="{'error--text':!item.lot}">{{item.lot || 'Required'}}</div>
                  </template>
                  <div v-else>n/a</div>
                </template>
                <template v-slot:item.fromSite="{item}">
                  <v-autocomplete v-if="selectedItem === item.key" v-model="item.fromSiteId" dense :items="sites" item-text="name" item-value="id" :rules="[fieldRules.required]" class="text-caption" :disabled="transaction.defaultFromSiteId!==''" outlined/>
                  <div v-else :class="{'error--text':!item.fromSiteId}">{{item.fromSiteId ? sites.find(site => site.id === item.fromSiteId).name : 'Required'}}</div>
                </template>
                <template v-slot:item.toSite="{item}">
                  <v-autocomplete v-if="selectedItem === item.key" v-model="item.toSiteId" dense :items="sites" item-text="name" item-value="id" :rules="[fieldRules.required]" class="text-caption" :disabled="transaction.defaultToSiteId!==''" outlined/>
                  <div v-else :class="{'error--text':!item.toSiteId}">{{item.toSiteId ? sites.find(site => site.id === item.toSiteId).name : 'Required'}}</div>
                </template>
                <template v-slot:item.quantity="{item}">
                  <decimal-number-input v-if="selectedItem === item.key" v-model="item.quantity" prefix="" :suffix="item.trackUnits" :precision="item.precision" class="text-caption"/>
                  <div v-else :class="{'error--text':item.quantity < 0}">
                    {{item.quantity | number(item.precision)}} {{item.trackUnits}}
                  </div>
                </template>
                <template v-slot:body.append="{headers}">
                  <tr class="no-hover" @click="selectedItem = null">
                    <td class="text-center">
                      <v-btn fab color="primary" x-small @click.stop="addItem()"><v-icon>mdi-plus</v-icon></v-btn>
                    </td>
                    <td :colspan="headers.length-1"/>
                  </tr>
                </template>
              </v-data-table>
            </outlined-block>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" counter="255"/>
            </v-col>
          </v-row>
          <v-row v-if="transaction.defaultFromSiteId && transaction.defaultToSiteId">
            <v-col cols="6">
              <v-textarea label="Special instructions" rows="2"
                  v-model="transaction.specialInstructions" outlined dense :rules="[fieldRules.maxLength(1000)]" hide-details="auto"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <v-spacer/>
        <v-btn plain color="primary" @click="cancel">Cancel</v-btn>
        <v-btn color="primary" outlined :loading="saving" @click="save(true)">Save as draft</v-btn>
        <v-btn color="primary" :loading="saving"  @click="save(false)">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'

export default {
  name: 'InventoryItemsBatchMoveEdit',
  mixins: [confirmLeave],
	props: {
    id: {type:String,required:false},
    copyId: {type:String,required:false}
	},
	data() {
		return {
      valid: true,
      headers: [{text:'',value:'action',width:'4%',align:'center',cellClass:'pa-0',divider:true},
                {text:'Item',value:'item',width:'24%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                {text:'Lot',value:'lot',width:'10%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                {text:'From site',value:'fromSite',width:'20%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                {text:'To site',value:'toSite',width:'20%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                {text:'Quantity',value:'quantity',width:'12%',align:'right',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'}],
      transaction: {date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),comments:'',status:'',defaultFromSiteId:'',defaultToSiteId:'',
                    number:'',pallets:0,shipVia:'',specialInstructions:''},
      items: [],
      shipVias: [],
      saving:false,
      allItems: [],
      selectedItem: null
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(true);
    },
    defaultSites() {
      return [{name:'no default',id:''},{divider:true}, ...this.$store.getters.sites(true)];
    }
  },
	async created() {
    const results = await this.$apiCall(`query InventoryItemsBatchMoveEdit($id:ID!, $edit:Boolean!) {
                                            shipVias {name}
                                            itemsBatchMove(id:$id) @include(if:$edit) {
                                              id,date,draft,status,comments,
                                              defaultFromSiteId,defaultToSiteId
                                              number,pallets,shipVia,specialInstructions
                                              items {itemId,lot,fromSiteId,toSiteId,quantity}
                                            }
                                          }`,
                                        {edit:!!this.id || !!this.copyId, id:this.id ?? this.copyId ?? ''});
    if (!results.error) {
      this.shipVias = results.data.shipVias.map(shipVia => shipVia.name);
      if (this.id || this.copyId) {
        this.transaction = this.id ? results.data.itemsBatchMove : {...results.data.itemsBatchMove,date:this.transaction.date, status:''};
        let key = Date.now();
        this.transaction.items.forEach(item => {
          item.key = key++;
          if (this.copyId)
            item.lot = '';
        });
        this.items = this.transaction.items;
      }
    }

    await this.refresh();
    this.$nextTick(() => this.$refs.form.validate());
  },

	methods: {
    scan({type, id, quantity, lot}) {
      if (type !== 'Item')
        return;
      lot = lot ?? '';
      let item = this.items.find(item => item.itemId === id && item.lot === lot);
      if (!item) {
        item = this.addItem();
        item.itemId = id;
        item.lot = lot;
      }
      item.quantity += quantity;
      this.selectItem(item);
    },
    itemName(item) {
      return this.allItems.find(i =>i.id === item.itemId)?.name ?? 'Required';
    },
    changeDefaultFromSiteId() {
      if (this.transaction.defaultFromSiteId)
        for (const item of this.items)
          item.fromSiteId = this.transaction.defaultFromSiteId;
    },
    changeDefaultToSiteId() {
      if (this.transaction.defaultToSiteId)
        for (const item of this.items)
          item.toSiteId = this.transaction.defaultToSiteId;
    },
    async refresh() {
      this.allItems = [];
      await this.$lazyListLoad(this.allItems, 'items2', 'id,name,lotTracking,trackUnits,precision,upc,sites {siteId,quantity,lot}', 100, [{field:'active', comparator:'eq', value:'true'},{field:'type',comparator:'eq',value:'Item'}], [{name:'asof', value:this.transaction.date}]);
      this.items.forEach(item => this.selectItem(item));
    },
    addItem() {
      const defaultSiteId = this.sites[0].id;
      const key = Date.now();
      const item = {itemId:'', lot:'',
                    fromSiteId:this.transaction.defaultFromSiteId || defaultSiteId,
                    toSiteId:this.transaction.defaultToSiteId || defaultSiteId,
                    quantity:0, key};
      this.items.push(item);
      this.selectedItem = key;
      this.$nextTick(() => this.$refs.form.validate());
      return item;
    },
    selectItem(item) {
      const sourceItem = this.allItems.find(i => i.id === item.itemId);
      if (sourceItem) {
        const sites = sourceItem.sites || [];
        item.lots = [...new Set(sites.map(site => site.lot))];
        item.lotTracking = sourceItem.lotTracking;
        item.precision = sourceItem.precision;
        item.trackUnits = sourceItem.trackUnits;
      }
      this.$nextTick(() => this.$refs.form.validate());
    },
    cancel() {
      this.$router.back();
    },
		async save(draft) {
      if (!this.valid ||
          this.items.some(item => !item.itemId ||
                                  (item.lotTracking && !item.lot) ||
                                   !item.fromSiteId || !item.toSiteId ||
                                  item.quantity < 0)) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.saving = true;
      this.transaction.items = this.items.map(item => {
        const {key,lotTracking,lots,trackUnits,precision, ...i} = item;
        return i;
      });
      this.transaction.draft = draft;
      const results = await this.$apiCall(`mutation InventoryItemsBatchMoveEdit($input: ItemBatchMoveInput!) {
                                              itemsBatchMoveCrupdate(input:$input) {id}
                                            }`,
                                          {input:this.transaction});
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`Completed bulk item move`});
      }
		}
	}
}
</script>